import { Flex, Grid, Separator } from '@radix-ui/themes'
import { useSuspenseQuery as useTanstackSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import EmptyBotsIcon from '~/assets/ai-30.svg?react'
import { Boundary, Page } from '~/componentsV2'
import { EmptyState, Icon, Input } from '~/elementsv2'
import { ContributionsSidebarSection, ViewAsSection, WorkspaceSocialHeader } from '~/features/workspaces/components'
import { useCreateBot } from '~/hooks'
import { SidebarLayout } from '~/layoutsV2'
import { workspacesQueryOptions } from '~/queries'
import { computeBotHistory } from '~/features/bots/services'
import { userPreferencesQueryOptions } from '~/queries/user'
import { getQueryOptions, useQuery } from '~/services'
import { AuditsPreview, BotCardSection, BotPreviewCard } from '~/features/botHome/components'
import { Card, Button } from '@bpinternal/ui-kit'
import { createBotPrompt } from '~/features/bots/services/createBotPrompt'
import { MembersPreview } from '../../../features/botHome/components/MembersPreview'
import { UsagePreview } from '../../../features/botHome/components/UsagePreview'
import { Clock, List, Plus, Search } from 'lucide-react'

export const Route = createFileRoute('/workspaces/$workspaceId/home')({
  component: WorkspaceComponent,
})

function WorkspaceComponent() {
  const { workspaceId } = Route.useParams()
  const workspace = useTanstackSuspenseQuery(getQueryOptions('workspaces_/$workspaceId_', { workspaceId })).data
  const { data: workspaces } = useTanstackSuspenseQuery(workspacesQueryOptions())

  return (
    <Page title={<WorkspaceSocialHeader workspace={workspace} />}>
      <SidebarLayout
        main={
          <Boundary showErrorIcon errorIconSize={5}>
            <BotList />
          </Boundary>
        }
        rightSidebar={
          <>
            <Boundary className="h-48" loaderSize={'7'}>
              <ViewAsSection currentRoute={Route.id} userWorkspaces={workspaces} />
            </Boundary>
            <Boundary className="h-48" loaderSize={'7'}>
              <UsagePreview workspaceId={workspaceId} />
              <Separator size={'4'} mt={'6'} mb={'4'} />
            </Boundary>
            <Boundary className="h-80" loaderSize={'7'}>
              <AuditsPreview workspaceId={workspaceId} />
              <Separator size={'4'} mt={'6'} mb={'4'} />
            </Boundary>
            <Boundary className="h-80" loaderSize={'7'}>
              <ContributionsSidebarSection userWorkspaces={workspaces} workspace={workspace} />
              <Separator size={'4'} mt={'6'} mb={'4'} />
            </Boundary>
            <Boundary className="h-24" loaderSize={'7'}>
              <MembersPreview workspaceId={workspaceId} />
            </Boundary>
          </>
        }
      />
    </Page>
  )
}

const BotList = () => {
  const { workspaceId } = Route.useParams()
  const workspace = useTanstackSuspenseQuery(getQueryOptions('workspaces_/$workspaceId_', { workspaceId })).data
  const { mutate: createBot, isPending } = useCreateBot(workspace.id)
  const [filter, setFilter] = useState('')
  const prefsProps = { path: '$workspaceId/botHistory', params: { workspaceId: workspace.id } } as const
  const { user } = Route.useRouteContext()

  const { data: bots = [] } = useQuery('workspaces_/$workspaceId_/bots_', { workspaceId: workspace.id })
  const recentBots = useTanstackSuspenseQuery(userPreferencesQueryOptions(prefsProps)).data.filter(
    (b) => !!bots.find((bot) => bot.id === b)
  )
  const botHistory = useMemo(() => computeBotHistory(recentBots), [recentBots])

  if (bots.length === 0) {
    return (
      <Card className="py-2">
        <EmptyState
          icon={EmptyBotsIcon}
          className="h-80"
          title="Workspace has no bots"
          description="Your workspace has no bots. Get started by creating a new bot"
          primaryAction={
            <Button
              color="grass"
              leading={<Plus size={20} />}
              onClick={() => void createBotPrompt().then(createBot)}
              loading={isPending}
              size="3"
            >
              Create Bot
            </Button>
          }
        />
      </Card>
    )
  }

  return (
    <Flex direction={'column'} gap={'4'} className="@container">
      {botHistory.length > 0 && (
        <BotCardSection title="Recent" icon={Clock}>
          <Grid gap={'2'} className="grid-cols-[repeat(auto-fit,minmax(300px,1fr))]">
            {botHistory.map((botId) => (
              <BotPreviewCard
                showChart={false}
                key={botId}
                workspaceId={workspaceId}
                userId={user.id}
                location="recent_bots"
                id={botId}
              />
            ))}
          </Grid>
        </BotCardSection>
      )}
      <BotCardSection title="Bots" icon={List}>
        <div className="flex w-full gap-2">
          <Input
            leading={<Icon color="gray" icon={Search} />}
            placeholder="Find a bot"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <Button
            loading={isPending}
            leading={<Plus strokeWidth={'3'} />}
            onClick={() => void createBotPrompt().then(createBot)}
          >
            Create Bot
          </Button>
        </div>
        <Grid gap={'2'} className="grid-cols-[repeat(auto-fit,minmax(300px,1fr))]">
          {bots
            .filter((b) => b.name.toLowerCase().includes(filter.toLowerCase()))
            .map((bot) => (
              <BotPreviewCard key={bot.id} workspaceId={workspaceId} userId={user.id} location="all_bots" {...bot} />
            ))}
          <Button
            variant="dashed"
            loading={isPending}
            onClick={() => void createBotPrompt().then(createBot)}
            size={'3'}
            radius="large"
            leading={<Plus strokeWidth={'2'} />}
            className="h-full min-h-[240px]" // Not very clean but magic number for when the button is in the last row
          >
            Create Bot
          </Button>
        </Grid>
      </BotCardSection>
    </Flex>
  )
}
